import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/login/link?token=` — where the e-mailed sign-in link lands. The token is
 * spent by a POST from here, not by the GET, so a mail scanner that
 * prefetches the link doesn't burn it.
 */
export const Route = createFileRoute("/login_/link")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: LoginLinkPage,
});

function LoginLinkPage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const [failed, setFailed] = useState(!token);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<{ hasPassword?: boolean }>("/api/auth/email-link/redeem", { token })
      .then(({ hasPassword }) => {
        window.location.href = hasPassword === false ? "/profile" : "/";
      })
      .catch(() => setFailed(true));
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm space-y-4 text-center">
        {failed ? (
          <>
            <h1 className="font-heading text-xl font-bold">{t("login.linkBroken")}</h1>
            <p className="text-sm text-muted-foreground">{t("login.linkBrokenHint")}</p>
            <Button asChild>
              <Link to="/login">{t("login.backToSignIn")}</Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("login.signingIn")}</p>
        )}
      </div>
    </div>
  );
}
